/**
 * useVsRobotFinish — VS Robot end-of-duel persistence.
 *
 * Triggers ONCE when the duel transitions to "won" or "lost":
 *   • If `elim_attempt` is in the URL → records the eliminatoria attempt via
 *     POST /play/finish (and SKIPS the regular VS Robot leaderboard write so
 *     elim duels don't accumulate ranking points).
 *   • Otherwise → POST /vs-robot/finish (writes to leaderboard) and fetches
 *     the user's current rank for the share dialog.
 *
 * Idempotent via the `finishedRef` flag injected by the caller.
 */
import { useEffect } from "react";
import { api } from "@/lib/api";
import { submitResult } from "@/lib/resultOutbox";

const countLetters = (arr) => arr.filter((p) => p !== "*").length;
const countStars = (arr) => arr.filter((p) => p === "*").length;

// Poll the (idempotent) finish endpoint until the server confirms the
// outcome with a numeric attempts_left. Used as a fallback when the first
// submit was queued offline or returned a minimal payload, so the result
// screen always resolves AND the win is guaranteed to land server-side.
async function ensureElimState(endpoint, payload, { tries = 10, intervalMs = 2000 } = {}) {
  for (let i = 0; i < tries; i++) {
    try {
      const { data } = await api.post(endpoint, payload, { timeout: 8000 });
      if (data && typeof data.attempts_left === "number") return data;
    } catch { /* keep polling */ }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return null;
}

export function useVsRobotFinish({
  mode, phase, session, params,
  gameId, profile,
  progress, robotProgress, round,
  finishedRef, onRank, onElimFinish,
}) {
  // Won OR lost — same handler structure, only the `winner` differs.
  useEffect(() => {
    if (mode !== "vs_robot") return;
    if (phase !== "won" && phase !== "lost") return;
    if (finishedRef.current || !session) return;
    finishedRef.current = true;
    const winner = phase === "won" ? "user" : "robot";

    (async () => {
      try {
        const elimAttemptId = params.get("elim_attempt");
        if (elimAttemptId) {
          // Eliminatória "Vencer Robot": record the outcome through the
          // AUTHENTICATED, self-contained endpoint. Unlike /play/finish it
          // does NOT require the in_progress attempt row to still exist — a
          // genuine win is recreated server-side if the row was lost, so a
          // victory can NEVER be silently dropped. Routed through the Result
          // Outbox so a network blip never costs the student their win.
          const elimId = params.get("elim");
          const elimPayload = {
            winner,
            attempt_id: elimAttemptId,
            tier_id: params.get("tier") || null,
            // Stable key shared by the outbox retry AND the ensureElimState
            // poll so the backend idempotency replay guarantees exactly one
            // recorded outcome (no duplicate recreated rows).
            client_submission_id: `elimrobot-${elimAttemptId}-${winner}`,
          };
          const endpoint = `/eliminatorias/${elimId}/play/finish-robot`;
          const result = await submitResult(endpoint, elimPayload, {
            description: `Eliminatória Robot — ${winner}`,
          });
          // Surface the server state (passed + attempts_left) to the result
          // screen so it shows the correct "Tentativas restantes: N" instead
          // of staying stuck on "A confirmar tentativas restantes…".
          let resp = result?.response;
          if (!resp || typeof resp.attempts_left !== "number") {
            // The first POST was queued (slow/flaky network) or returned a
            // minimal payload. Poll the idempotent endpoint until we get the
            // real attempts_left so the win is guaranteed to be persisted.
            resp = await ensureElimState(endpoint, elimPayload);
          }
          if (resp) onElimFinish?.(resp);
          return;
        }
        // Regular VS ROBOT mode (ranking).
        await submitResult("/vs-robot/finish", {
          session_id: session.session_id,
          player_name: profile.name,
          school: profile.school || "",
          game_id: gameId,
          robot_id: params.get("robot"),
          winner,
          user_letters: countLetters(progress),
          user_stars: countStars(progress),
          robot_letters: countLetters(robotProgress),
          robot_stars: countStars(robotProgress),
          rounds: round,
        }, { description: `VS Robot — ${profile.name} vs ${params.get("robot") || "?"}` });
        const { data } = await api.get("/vs-robot/my-rank", {
          params: { player_name: profile.name },
        });
        onRank?.(data);
      } catch (e) { /* non-blocking */ }
    })();
  }, [mode, phase, session, params, gameId, profile.name, profile.school, progress, robotProgress, round, finishedRef, onRank, onElimFinish]);
}
